/** FadeOutHandler and cosmetic helpers (Oscillator). */
import { GameBehaviour } from '../game.ts';
import type { SimGo } from '../world.ts';
import { FrameCounter, lerp } from '../unity.ts';
import { num } from '../props.ts';
import { ActivatorBase } from './activators.ts';

export class FadeOutHandler extends GameBehaviour {
  activator: ActivatorBase | null = null;
  fadeCounter = FrameCounter.seconds(0.5);
  oldActive = false;
  isActive = false;
  renderers: SimGo[] = [];
  tilemaps: SimGo[] = [];
  startAlphaValues: number[] = [];
  override noSave = ['activator', 'renderers', 'tilemaps'];

  override awake() {
    this.renderers = this.go.descendants().filter((g) => g.sprite && g.active);
    this.tilemaps = this.go.descendants().filter((g) => g.tilemap && g.active);
    this.updateStartAlphaValues();
  }

  override start() {
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
  }

  override fixedUpdate() {
    if (!this.activator) return;
    this.oldActive = this.isActive;
    this.isActive = this.activator.isActivated || this.isActive;
    if (this.fadeCounter.isNotRunning() && !this.oldActive && this.isActive) this.startFade();
    if (this.fadeCounter.isRunningNotFinished()) this.updateFade();
    if (this.fadeCounter.countCheckFinishReset()) this.finishFade();
  }

  private startFade() {
    this.fadeCounter.start();
    this.sound('fadeOut');
    for (const c of this.go.collidersInChildren()) c.enabled = false;
    this.updateStartAlphaValues();
  }

  private updateStartAlphaValues() {
    this.startAlphaValues = [...this.renderers.map((g) => g.sprite!.color[3]), ...this.tilemaps.map((g) => g.tilemap!.color[3])];
  }

  private updateFade() {
    const t = this.fadeCounter.percentage;
    let i = 0;
    for (const g of this.renderers) {
      const c = g.sprite!.color;
      g.sprite!.color = [c[0], c[1], c[2], lerp(this.startAlphaValues[i++], 0, t)];
    }
    for (const g of this.tilemaps) {
      const c = g.tilemap!.color;
      g.tilemap!.color = [c[0], c[1], c[2], lerp(this.startAlphaValues[i++], 0, t)];
    }
  }

  private finishFade() {
    for (const g of this.renderers) g.sprite!.color = [g.sprite!.color[0], g.sprite!.color[1], g.sprite!.color[2], 0];
    for (const g of this.tilemaps) g.tilemap!.color = [g.tilemap!.color[0], g.tilemap!.color[1], g.tilemap!.color[2], 0];
  }
}

/** Oscillator runs in Update(): applied as a render-only offset so it never disturbs physics. */
export class Oscillator extends GameBehaviour {
  static override checkpointable = false;
  target: SimGo | null = null;
  amplitude = 0.055;
  frequency = 0.7;
  offset = 0;
  elapsed = 0;
  override noSave = ['target'];
  override awake() {
    const d = this.data;
    this.amplitude = num(d.amplitude, 0.055);
    this.frequency = num(d.frequency, 0.7);
    this.offset = num(d.offset, 0);
    const xr = num(d.xRangeForFullPhase, 75);
    if (xr !== 0) this.offset += (this.go.position.x % xr) / xr;
  }
  override start() {
    this.target = this.refGo(this.data.transformToMove) ?? this.go;
  }
  override update(dt: number) {
    this.elapsed += dt;
    const t = (this.elapsed * this.frequency + this.offset) * 2 * Math.PI;
    if (this.target) this.target.renderOffset = { x: 0, y: Math.sin(t) * this.amplitude };
  }
}
